<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { searchSiteLanguages, SITE_LANGUAGES } from '../../utils/siteLanguages'

// inline: true면 헤더 등 레이아웃 안에 끼워 넣는 용도(position: fixed 안 씀).
// 기본값(false)은 화면에 떠 있는 플로팅 버튼.
const props = withDefaults(defineProps<{ inline?: boolean }>(), { inline: false })

const translation = useSiteTranslationStore()
const { currentLang, isTranslating, errorMessage } = storeToRefs(translation)

const isOpen = ref(false)
const searchQuery = ref('')
const searchInputRef = ref<HTMLInputElement | null>(null)
const filteredLanguages = computed(() => searchSiteLanguages(searchQuery.value))
const menuId = useId()
const toggleBtnRef = ref<HTMLButtonElement | null>(null)
// 드롭다운을 body에 teleport하므로, 버튼 기준 화면 좌표(px)를 직접 계산해서 위치를 정한다.
// inline 모드에서 헤더/드로어 같은 부모가 overflow:hidden 등으로 메뉴를 잘라내는 문제를 피하는 용도.
const menuStyle = ref<{ top: string, right: string }>({ top: '0px', right: '0px' })

function updateMenuPosition() {
  const btn = toggleBtnRef.value
  if (!btn)
    return
  const rect = btn.getBoundingClientRect()
  const menuWidth = Math.min(360, window.innerWidth - 24)
  const maxRight = window.innerWidth - menuWidth - 12
  menuStyle.value = {
    top: `${Math.round(rect.bottom + 8)}px`,
    right: `${Math.max(12, Math.min(Math.round(window.innerWidth - rect.right), maxRight))}px`,
  }
}

async function toggleMenu() {
  if (isOpen.value) {
    isOpen.value = false
    return
  }
  updateMenuPosition()
  searchQuery.value = ''
  isOpen.value = true
  await nextTick()
  searchInputRef.value?.focus()
}

function closeMenu() {
  const wasOpen = isOpen.value
  isOpen.value = false
  if (wasOpen)
    toggleBtnRef.value?.focus()
}
const currentLabel = computed(() => SITE_LANGUAGES.find(l => l.code === currentLang.value)?.label || 'Korean (Original)')

async function selectLanguage(code: string) {
  closeMenu()
  await translation.selectLanguage(code)
}

// 메뉴는 position: fixed라 페이지 스크롤에 영향을 안 받는다(버튼이 보통 고정 헤더
// 안에 있어 스크롤해도 같이 안 움직임) - 그래서 스크롤로는 닫지 않는다. 드롭다운
// 리스트 내부를 스크롤할 때도 이 이벤트가 같이 잡혀서 바로 닫혀버리는 문제가 있었다.
// 창 크기 자체가 바뀌는 resize만 위치가 어긋날 수 있어 그때만 닫는다.
function handleResize() {
  if (isOpen.value)
    closeMenu()
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<template>
  <div class="site-translate-widget no-translate" :class="{ 'is-inline': props.inline }" lang="en" translate="no">
    <button
      ref="toggleBtnRef"
      type="button"
      class="toggle-btn"
      :aria-expanded="isOpen"
      :aria-controls="isOpen ? menuId : undefined"
      aria-haspopup="dialog"
      :aria-label="`Choose language: ${currentLabel}`"
      @click="toggleMenu"
    >
      <span class="globe-icon">🌐</span>
      <span class="current-label">{{ currentLabel }}</span>
      <span v-if="isTranslating" class="spinner" aria-hidden="true" />
    </button>

    <!-- 드롭다운을 body로 teleport한다 - inline 모드로 헤더/드로어 안에 끼워 넣으면
         부모 쪽 overflow/스택 컨텍스트 때문에 메뉴가 잘려 보이는 문제를 피하기 위함. -->
    <Teleport to="body">
      <div v-if="isOpen" class="backdrop" @click="closeMenu" />
      <div
        v-if="isOpen"
        :id="menuId"
        class="no-translate lang-menu"
        role="dialog"
        aria-label="Choose your language"
        lang="en"
        dir="ltr"
        translate="no"
        :style="menuStyle"
        @keydown.esc.stop.prevent="closeMenu"
      >
        <div class="lang-search">
          <p v-if="errorMessage" role="alert">
            {{ errorMessage }}
            <button type="button" class="lang-item" @click="selectLanguage(currentLang)">
              Retry translation
            </button>
          </p>
          <label :for="`${menuId}-search`">Find your language</label>
          <input
            :id="`${menuId}-search`"
            ref="searchInputRef"
            v-model="searchQuery"
            type="search"
            placeholder="Search country or language"
            autocomplete="off"
            :aria-controls="`${menuId}-results`"
          >
          <p role="status">
            {{ filteredLanguages.length }} / {{ SITE_LANGUAGES.length }} languages
          </p>
        </div>
        <div :id="`${menuId}-results`" class="lang-results">
          <button
            v-for="lang in filteredLanguages"
            :key="lang.code"
            type="button"
            class="lang-item"
            :class="{ 'is-active': lang.code === currentLang }"
            :aria-pressed="lang.code === currentLang"
            @click="selectLanguage(lang.code)"
          >
            <span class="lang-name">{{ lang.label }}</span>
            <span class="lang-native" dir="auto">{{ lang.nativeName }}</span>
          </button>
          <p v-if="!filteredLanguages.length" class="lang-empty">
            No languages found. Try another country or language name.
          </p>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.site-translate-widget {
  position: fixed;
  right: 20px;
  top: 76px;
  z-index: 2147483000; /* 채널톡(999999)보다 낮지만 일반 UI보다는 확실히 위 */
}

/* inline 모드: 헤더/드로어 등 레이아웃 안에 자연스럽게 끼워 넣는다(플로팅 아님). */
.site-translate-widget.is-inline {
  position: relative;
  top: auto;
  right: auto;
  left: auto;
  bottom: auto;
  z-index: auto;
  display: inline-flex;
}

.toggle-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  background: #111;
  color: #fff;
  border: none;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
}

.toggle-btn:hover {
  background: #2a2a2a;
}

.globe-icon {
  font-size: 15px;
}

.current-label {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.spinner {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: site-translate-spin 0.7s linear infinite;
}

@keyframes site-translate-spin {
  to {
    transform: rotate(360deg);
  }
}

.backdrop {
  position: fixed;
  inset: 0;
  z-index: 2147482999;
}

/* body로 teleport되므로 top/right는 JS(menuStyle)가 버튼 위치 기준으로 계산해 인라인으로 넣는다. */
.lang-menu {
  position: fixed;
  display: flex;
  flex-direction: column;
  width: min(360px, calc(100vw - 24px));
  max-height: min(520px, calc(100dvh - v-bind('menuStyle.top') - 12px));
  overflow: hidden;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18);
  padding: 6px;
  z-index: 2147483000;
}

.lang-search {
  flex: none;
  padding: 12px;
  color: #111;
  border-bottom: 1px solid #ddd;
}

.lang-search label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 700;
}

.lang-search input {
  width: 100%;
  padding: 10px;
  border: 1px solid #777;
  border-radius: 8px;
  background: #fff;
  color: #111;
  font-size: 16px;
}

.lang-search p {
  margin: 8px 0 0;
  color: #555;
  font-size: 12px;
}

.lang-results {
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.lang-name,
.lang-native {
  display: block;
  overflow-wrap: anywhere;
}

.lang-name {
  font-weight: 700;
}

.lang-native {
  margin-top: 3px;
  color: #555;
  font-size: 12px;
}

.lang-empty {
  padding: 20px 12px;
  color: #555;
  font-size: 14px;
}

.lang-item:focus-visible,
.lang-search input:focus-visible {
  outline: 2px solid #111;
  outline-offset: -2px;
}

.lang-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 9px 12px;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-size: 13px;
  color: #111;
  cursor: pointer;
}

.lang-item:hover {
  background: #f2f2f2;
}

.lang-item.is-active {
  background: #fee500;
  font-weight: 700;
}

@media (max-width: 599px) {
  .site-translate-widget {
    right: 10px;
    top: 64px;
  }
  .current-label {
    max-width: 72px;
  }
}
</style>
